import { useEffect, useState, type ReactNode } from 'react';
import { ChevronRight, Plus } from 'lucide-react';
import { ChatChannelResponse } from '@/lib/api';
import { cn } from '@/lib/utils';
import { seededGradient } from '../utils';
import { UnreadBadges } from './UnreadBadges';

/** Unread/mention rollup for a set of channels, ignoring muted and left ones —
 *  a left channel can't be opened to clear its badge, so counting it would pin
 *  a permanent unread on the group. */
function rollup(channels: ChatChannelResponse[]): { unread: number; hasMention: boolean } {
  const audible = channels.filter((c) => !c.isMuted && !c.hasLeft);
  return {
    unread: audible.reduce((sum, c) => sum + (c.unreadCount ?? 0), 0),
    hasMention: audible.some((c) => (c.mentionCount ?? 0) > 0),
  };
}

interface SubGroupProps {
  id: string;
  label: string;
  channels: ChatChannelResponse[];
  defaultExpanded: boolean;
  emptyLabel: string;
  renderChannel: (channel: ChatChannelResponse) => ReactNode;
  /** Renders a trailing action in the header (e.g. "new DM"). */
  action?: { label: string; onClick: () => void };
  /** Forces the tier open whenever the active channel lives inside it, so a
   *  freshly opened conversation is never highlighted behind a collapsed tier. */
  activeChannelId?: string | null;
  /** Held open regardless of the user's toggle (a filtered list hides nothing). */
  forceExpanded?: boolean;
}

/**
 * One collapsible tier inside a guild — Channels or Direct Messages.
 *
 * Expansion here is deliberately component-local rather than persisted: the
 * guild node itself carries the remembered state, and persisting a second tier
 * per guild would multiply stored keys for a preference users express by simply
 * looking at the list. The default differs per tier (channels open, DMs shut).
 */
function SubGroup({
  id,
  label,
  channels,
  defaultExpanded,
  emptyLabel,
  renderChannel,
  action,
  activeChannelId,
  forceExpanded = false,
}: SubGroupProps) {
  const [expandedByUser, setExpanded] = useState(defaultExpanded);
  const expanded = forceExpanded || expandedByUser;
  const { unread, hasMention } = rollup(channels);

  // Re-open when the ACTIVE channel moves into this tier (e.g. a guild DM just
  // opened from the member picker) — the selection would otherwise be invisible.
  // Keyed on the active channel id, not on `containsActive` alone, so switching
  // between two conversations in the tier re-expands even after a manual
  // collapse; it never collapses on the user's behalf.
  const containsActive =
    activeChannelId != null && channels.some((c) => c.channelId === activeChannelId);
  useEffect(() => {
    if (containsActive) setExpanded(true);
  }, [activeChannelId, containsActive]);

  return (
    <div>
      <div className="flex items-center gap-1 pr-2">
        <button
          type="button"
          // Inert while forced open: a click would only flip the remembered
          // state behind an unchanged view, to surface later as a surprise.
          onClick={forceExpanded ? undefined : () => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-disabled={forceExpanded || undefined}
          aria-controls={id}
          className="flex-1 min-w-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-left transition-colors hover:bg-foreground/10 cursor-pointer"
        >
          <ChevronRight
            aria-hidden="true"
            className={cn(
              'flex-shrink-0 h-3 w-3 text-muted-foreground transition-transform',
              expanded && 'rotate-90',
            )}
          />
          <span className="flex-1 min-w-0 truncate text-[10px] font-normal text-muted-foreground uppercase tracking-wider">
            {label}
          </span>
          {!expanded && <UnreadBadges unread={unread} hasMention={hasMention} />}
        </button>
        {action && (
          <button
            type="button"
            aria-label={action.label}
            title={action.label}
            onClick={action.onClick}
            className="flex-shrink-0 h-5 w-5 rounded flex items-center justify-center text-muted-foreground hover:bg-foreground/10 cursor-pointer"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {expanded && (
        <div id={id} className="flex flex-col gap-1">
          {channels.length === 0 ? (
            <div className="px-4 py-1.5 text-xs text-muted-foreground">{emptyLabel}</div>
          ) : (
            channels.map(renderChannel)
          )}
        </div>
      )}
    </div>
  );
}

interface GuildChannelGroupProps {
  guildId: string;
  /** Guild display name — also seeds the icon gradient. */
  name: string;
  /** The guild's group/private channels. */
  channels: ChatChannelResponse[];
  /** DMs scoped to this guild (work talk), distinct from personal DMs. */
  directChannels: ChatChannelResponse[];
  expanded: boolean;
  onToggle: () => void;
  renderChannel: (channel: ChatChannelResponse) => ReactNode;
  /** Opens the guild-member picker for starting a guild-scoped DM. */
  onNewDirectMessage?: () => void;
  /** The selected channel — auto-opens the tier that contains it. */
  activeChannelId?: string | null;
  /**
   * Held open, both the node and its tiers, regardless of the remembered
   * toggle — while the list is filtered every match must be visible. The
   * toggles are inert meanwhile (`onToggle` is not called), so the persisted
   * expansion state cannot change behind a view that would not reflect it.
   */
  forceExpanded?: boolean;
}

/**
 * A guild rendered as one collapsible node in the chat list, holding two tiers:
 * its channels and its guild-scoped DMs. Everything filed under the guild lives
 * here instead of being scattered through the flat sections.
 *
 * Collapsed, the node carries the guild's rolled-up unread count so a closed
 * guild can still signal activity. Muted channels are excluded from that roll-up
 * to match the worktop/header badge, which also skips them — otherwise a muted
 * channel would keep pulling attention from the one place it can't be silenced.
 */
export function GuildChannelGroup({
  guildId,
  name,
  channels,
  directChannels,
  expanded: expandedByUser,
  onToggle,
  renderChannel,
  onNewDirectMessage,
  activeChannelId,
  forceExpanded = false,
}: GuildChannelGroupProps) {
  const expanded = forceExpanded || expandedByUser;
  const { unread, hasMention } = rollup([...channels, ...directChannels]);
  const initial = name.charAt(0).toUpperCase() || '?';
  const panelId = `guild-channels-${guildId}`;

  return (
    <div>
      <button
        type="button"
        onClick={forceExpanded ? undefined : onToggle}
        aria-expanded={expanded}
        aria-disabled={forceExpanded || undefined}
        aria-controls={panelId}
        className="w-full flex items-center gap-2 px-4 py-2 rounded-lg text-left transition-colors hover:bg-foreground/10 cursor-pointer"
      >
        <ChevronRight
          aria-hidden="true"
          className={cn(
            'flex-shrink-0 h-4 w-4 text-muted-foreground transition-transform',
            expanded && 'rotate-90',
          )}
        />
        <span
          aria-hidden="true"
          className="flex-shrink-0 h-7 w-7 rounded-md flex items-center justify-center text-xs font-semibold text-white"
          style={{ background: seededGradient(name) }}
        >
          {initial}
        </span>
        <span className="flex-1 min-w-0 truncate text-sm font-medium text-foreground">{name}</span>
        {/* Only the collapsed node carries the rollup — expanded, the per-channel
            badges below already show the same counts. */}
        {!expanded && <UnreadBadges unread={unread} hasMention={hasMention} />}
      </button>
      {expanded && (
        <div id={panelId} className="ml-5 pl-2 border-l border-border flex flex-col gap-0.5">
          <SubGroup
            id={`guild-tier-channels-${guildId}`}
            label="Channels"
            channels={channels}
            defaultExpanded
            emptyLabel="No channels yet"
            renderChannel={renderChannel}
            activeChannelId={activeChannelId}
            forceExpanded={forceExpanded}
          />
          <SubGroup
            id={`guild-tier-dms-${guildId}`}
            label="Direct Messages"
            channels={directChannels}
            defaultExpanded={directChannels.some((c) => c.channelId === activeChannelId)}
            emptyLabel="No guild messages yet"
            renderChannel={renderChannel}
            activeChannelId={activeChannelId}
            forceExpanded={forceExpanded}
            action={
              onNewDirectMessage
                ? { label: `New message in ${name}`, onClick: onNewDirectMessage }
                : undefined
            }
          />
        </div>
      )}
    </div>
  );
}
